import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import EnvironmentBanner from './components/EnvironmentBanner';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Feed from './pages/Feed';
import Dashboard from './pages/Dashboard';
import GroupDetail from './pages/GroupDetail';
import PetDetail from './pages/PetDetail';
import ChildDetail from './pages/ChildDetail';
import PersonDetail from './pages/PersonDetail';
import MyWishes from './pages/MyWishes';
import MyPurchases from './pages/MyPurchases';
import Profile from './pages/Profile';
import AcceptInvitation from './pages/AcceptInvitation';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Support from './pages/Support';

// Protected Route Component
function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-gray-600">Loading...</div>
            </div>
        );
    }

    return user ? children : <Navigate to="/login" />;
}

// Public Route Component (redirect to dashboard if already logged in)
function PublicRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-gray-600">Loading...</div>
            </div>
        );
    }

    return user ? <Navigate to="/feed" /> : children;
}

function AppRoutes() {
    return (
        <Routes>
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                }
            />
            <Route
                path="/register"
                element={
                    <PublicRoute>
                        <Register />
                    </PublicRoute>
                }
            />
            <Route
                path="/feed"
                element={
                    <ProtectedRoute>
                        <Feed />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/group/:groupId"
                element={
                    <ProtectedRoute>
                        <GroupDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/child/:childId"
                element={
                    <ProtectedRoute>
                        <ChildDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/pet/:petId"
                element={
                    <ProtectedRoute>
                        <PetDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/person/:userId"
                element={
                    <ProtectedRoute>
                        <PersonDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/wishes"
                element={
                    <ProtectedRoute>
                        <MyWishes />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/purchases"
                element={
                    <ProtectedRoute>
                        <MyPurchases />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/profile"
                element={
                    <ProtectedRoute>
                        <Profile />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/forgot-password"
                element={
                    <PublicRoute>
                        <ForgotPassword />
                    </PublicRoute>
                }
            />
            {/* Deliberately NOT wrapped in PublicRoute: the link in the reset
                email must always open this page, even if some account is
                already signed in on this browser. */}
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/invite/:token" element={<AcceptInvitation />} />
            {/* Legal pages: fully public, and deliberately NOT wrapped in
                PublicRoute — that bounces signed-in visitors to /feed, but these
                must open for anyone, signed in or not, including App Review. They
                are the Privacy Policy / Support URLs given to App Store Connect and
                linked from the iOS Profile screen. Registered before the catch-all
                below, which would otherwise swallow them. */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/support" element={<Support />} />
            {/* The public face of the site. Wrapped in PublicRoute so a signed-in
                visitor still lands on /feed rather than reading the sales pitch —
                but a signed-OUT visitor now gets a page explaining what Wishful is
                instead of being bounced to a login wall. This is the App Store
                marketing URL and where invite links send people who don't have an
                account yet. See ../../listful-api/docs/LAUNCH_READINESS.md §2. */}
            <Route
                path="/"
                element={
                    <PublicRoute>
                        <Landing />
                    </PublicRoute>
                }
            />
            {/* Unknown paths go to the landing page, not to /feed: a signed-out
                visitor who mistypes should see what this site IS, and a signed-in
                one is forwarded on to /feed by PublicRoute anyway. */}
            <Route path="*" element={<Navigate to="/" />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            {/* Banner sits OUTSIDE AuthProvider so it shows even before auth loads. */}
            <EnvironmentBanner />
            <AuthProvider>
                <AppRoutes />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
